/**
 * Get the query string from a URL
 *
 * The query string is everything following the question mark
 *
 * @public Part of the API used by integrations - backward compatibility mandatory.
 *
 * @param {string} url The URL to parse
 * @returns {string} The query string
 */
function getQueryString(url) {
    // The magic: create a new anchor element, and set the URL as its href attribute.
    const a = document.createElement('a');
    a.href = url;

    return a.search;
}

/**
 * Stringify a parameters object to a query string
 *
 * Thanks to https://locutus.io/php/url/http_build_query/
 *
 * @param {{}} queryParams The parameter map
 * @returns {{}} The stringified query string
 */
function stringifyQueryParams(queryParams) {
    let value;
    let key;
    const tmp = [];
    const _httpBuildQueryHelper = function (key, val, argSeparator) {
        let k;
        const tmp = [];
        if (val === true) {
            val = '1';
        } else if (val === false) {
            val = '0';
        }
        if (val !== null) {
            if (typeof val === 'object') {
                for (k in val) {
                    if (val[k] !== null) {
                        tmp.push(_httpBuildQueryHelper(key + '[' + k + ']', val[k], argSeparator));
                    }
                }
                return tmp.join(argSeparator);
            } else if (typeof val !== 'function') {
                return encodeURIComponent(key) + '=' + encodeURIComponent(val);
            } else {
                throw new Error('There was an error processing for stringifyQueryParams().');
            }
        } else {
            return '';
        }
    };

    for (key in queryParams) {
        value = queryParams[key];
        const query = _httpBuildQueryHelper(key, value, '&');
        if (query !== '') {
            tmp.push(query);
        }
    }
    return tmp.join('&');
}

/**
 * Parse a query string into a parameter map
 *
 * Thanks to https://locutus.io/php/strings/parse_str/
 *
 * @public Part of the API used by integrations - backward compatibility mandatory.
 *
 * @param {String} queryString The query string to parse
 * @returns {{}} The parameter map
 */
function parseQueryString(queryString) {
    /* eslint-disable */
    var requestParameters = {};

    /*
     * Remove any leading ? as it is not part of the query string but will be passed due to the way we use
     * parseQueryString
     */
    if (typeof queryString !== 'undefined') {
        queryString = queryString.replace(/^\?/, '');
    } else {
        queryString = '';
    }

    var strArr = String(queryString)
            .replace(/^&/, '')
            .replace(/&$/, '')
            .split('&'),
        sal = strArr.length,
        i, j, ct, p, lastObj, obj, lastIter, undef, chr, tmp, key, value,
        postLeftBracketPos, keys, keysLen,
        fixStr = function(queryString) {
            const encodedQueryStringWithoutPluses = queryString.replace(/\+/g, '%20');
            try {
                return decodeURIComponent(encodedQueryStringWithoutPluses);
            } catch (e) {
                if (e instanceof URIError) {
                    // Contains invalid escape sequence. This is likely due to non-UTF-8 character encoding,
                    // which aren't supported by any decent modern tools. Use deprecated stuff in this case.
                    return unescape(encodedQueryStringWithoutPluses);
                }

                throw e;
            }
        };

    for (i = 0; i < sal; i++) {
        tmp = strArr[i].split('=');
        key = fixStr(tmp[0]);

        // Prevent Object.prototype pollution
        if (!key.includes('__proto__')) {

            value = (tmp.length < 2) ? '' : fixStr(tmp[1]);

            while (key.charAt(0) === ' ') {
                key = key.slice(1);
            }
            if (key.indexOf('\x00') > -1) {
                key = key.slice(0, key.indexOf('\x00'));
            }
            if (key && key.charAt(0) !== '[') {
                keys = [];
                postLeftBracketPos = 0;
                for (j = 0; j < key.length; j++) {
                    if (key.charAt(j) === '[' && !postLeftBracketPos) {
                        postLeftBracketPos = j + 1;
                    } else if (key.charAt(j) === ']') {
                        if (postLeftBracketPos) {
                            if (!keys.length) {
                                keys.push(key.slice(0, postLeftBracketPos - 1));
                            }
                            keys.push(key.substr(postLeftBracketPos, j - postLeftBracketPos));
                            postLeftBracketPos = 0;
                            if (key.charAt(j + 1) !== '[') {
                                break;
                            }
                        }
                    }
                }
                if (!keys.length) {
                    keys = [key];
                }
                for (j = 0; j < keys[0].length; j++) {
                    chr = keys[0].charAt(j);
                    if (chr === ' ' || chr === '.' || chr === '[') {
                        keys[0] = keys[0].substr(0, j) + '_' + keys[0].substr(j + 1);
                    }
                    if (chr === '[') {
                        break;
                    }
                }

                obj = requestParameters;
                for (j = 0, keysLen = keys.length; j < keysLen; j++) {
                    key = keys[j].replace(/^['"]/, '')
                        .replace(/['"]$/, '');
                    lastIter = j !== keys.length - 1;
                    lastObj = obj;
                    if ((key !== '' && key !== ' ') || j === 0) {
                        if (obj[key] === undef) {
                            obj[key] = {};
                        }
                        obj = obj[key];
                    } else { // To insert new dimension
                        ct = -1;
                        for (p in obj) {
                            if (obj.hasOwnProperty(p)) {
                                if (+p > ct && p.match(/^\d+$/g)) {
                                    ct = +p;
                                }
                            }
                        }
                        key = ct + 1;
                    }
                }
                lastObj[key] = value;
            }
        }
    }

    /* eslint-enable */
    return requestParameters;
}

/**
 * Add parameter object as query string to given url
 *
 * @param {String} url The URL the parameters should be added
 * @param {{}} params The query parameters to append
 * @return {string} The URL with appended parameters
 */
function addQueryParams(url, params) {
    const queryString = stringifyQueryParams(params);

    return url.indexOf('?') > -1 ? url + '&' + queryString : url + '?' + queryString;
}

export default {
    getQueryString: getQueryString,
    stringifyQueryParams: stringifyQueryParams,
    parseQueryString: parseQueryString,
    addQueryParams: addQueryParams,
};
